/**
 * WebView session persistence — survives native app process kills.
 *
 * When the OS kills the WebView (memory/battery), React reloads from scratch.
 * This module saves lightweight UI state to localStorage (keyed by sessionId)
 * so users resume at their last step instead of Terms & Conditions.
 *
 * Also deduplicates SESSION_START Sentry logs and reports storage reliability.
 * See docs/session-persistence.md for full design.
 */
import type { ResultStatus } from 'types/imageRecognition.d'
import type { ResultActionType } from 'types/resultActions'

const SESSION_KEY_PREFIX = 'bmt_session_'
const TIMER_KEY_PREFIX = 'step'
const PERSISTENCE_MARKER_KEY = 'bmt_storage_persistence_marker'
const SESSION_ID_ERROR_SENTINEL = 'SessionIdError'
/** Stale sessions auto-expire after 30 min — prevents restoring abandoned tests */
const SESSION_TTL_MS = 30 * 60 * 1000

export type StorageStatus = 'available' | 'unverified' | 'disabled'

export interface PersistedSessionState {
  currentStep: number
  result: ResultStatus | null
  resultAction: ResultActionType | undefined
  notSureCount: number
  sessionStartLogged: boolean
  testType: string
  timestamp: number
}

const buildSessionKey = (sessionId: string): string =>
  `${SESSION_KEY_PREFIX}${sessionId}`

const isSessionKey = (key: string): boolean =>
  key.startsWith(SESSION_KEY_PREFIX)

const isTimerKey = (key: string): boolean =>
  key.startsWith(TIMER_KEY_PREFIX) && key.includes('Timer_')

const tryLocalStorage = (): boolean => {
  try {
    const testKey = '__storage_test__'
    localStorage.setItem(testKey, '1')
    localStorage.removeItem(testKey)
    return true
  } catch {
    return false
  }
}

/** Rejects SessionIdError sentinel and empty ids — used before any localStorage write */
export const isValidSessionId = (sessionId: string): boolean =>
  Boolean(sessionId) && sessionId !== SESSION_ID_ERROR_SENTINEL

/** Saves current step, result, and related UI state. Preserves sessionStartLogged flag. */
export const persistSessionState = (
  sessionId: string,
  state: Omit<PersistedSessionState, 'timestamp' | 'sessionStartLogged'>
): void => {
  if (!isValidSessionId(sessionId) || !tryLocalStorage()) {
    return
  }

  try {
    const key = buildSessionKey(sessionId)
    const existing = readRawSession(sessionId)
    const payload: PersistedSessionState = {
      ...state,
      sessionStartLogged: existing?.sessionStartLogged ?? false,
      timestamp: Date.now(),
    }
    localStorage.setItem(key, JSON.stringify(payload))
  } catch (error) {
    console.warn('[sessionPersistence] Failed to persist state:', error)
  }
}

/** Reads persisted state; returns null if missing, expired, or localStorage unavailable */
export const restoreSessionState = (
  sessionId: string
): PersistedSessionState | null => {
  if (!isValidSessionId(sessionId) || !tryLocalStorage()) {
    return null
  }

  try {
    const data = readRawSession(sessionId)
    if (!data) {
      return null
    }

    if (Date.now() - data.timestamp > SESSION_TTL_MS) {
      clearSessionState(sessionId)
      return null
    }

    return data
  } catch (error) {
    console.warn('[sessionPersistence] Failed to restore state:', error)
    return null
  }
}

/** Marks SESSION_START as logged so WebView reload does not duplicate the Sentry event */
export const markSessionStartLogged = (sessionId: string): void => {
  if (!isValidSessionId(sessionId) || !tryLocalStorage()) {
    return
  }

  try {
    const key = buildSessionKey(sessionId)
    const existing = readRawSession(sessionId)

    if (existing) {
      existing.sessionStartLogged = true
      existing.timestamp = Date.now()
      localStorage.setItem(key, JSON.stringify(existing))
    } else {
      const payload: PersistedSessionState = {
        currentStep: 0,
        result: null,
        resultAction: undefined,
        notSureCount: 0,
        sessionStartLogged: true,
        testType: '',
        timestamp: Date.now(),
      }
      localStorage.setItem(key, JSON.stringify(payload))
    }
  } catch (error) {
    console.warn('[sessionPersistence] Failed to mark session start:', error)
  }
}

/** Returns whether SESSION_START was already logged for this session (survives WebView reload) */
export const isSessionStartLogged = (sessionId: string): boolean => {
  if (!isValidSessionId(sessionId) || !tryLocalStorage()) {
    return false
  }

  try {
    const data = readRawSession(sessionId)
    if (!data) {
      return false
    }

    if (Date.now() - data.timestamp > SESSION_TTL_MS) {
      clearSessionState(sessionId)
      return false
    }

    return data.sessionStartLogged === true
  } catch {
    return false
  }
}

/** Removes session payload and session-scoped timer keys for the given sessionId */
export const clearSessionState = (sessionId: string): void => {
  if (!isValidSessionId(sessionId) || !tryLocalStorage()) {
    return
  }

  try {
    localStorage.removeItem(buildSessionKey(sessionId))
    clearTimerKeysForSession(sessionId)
  } catch (error) {
    console.warn('[sessionPersistence] Failed to clear state:', error)
  }
}

/** Validates parsed localStorage JSON before restore or purge */
const isValidPersistedState = (data: unknown): data is PersistedSessionState => {
  if (!data || typeof data !== 'object') {
    return false
  }

  const d = data as Record<string, unknown>

  if (
    typeof d.currentStep !== 'number' ||
    d.currentStep < 0 ||
    d.currentStep > 6 ||
    typeof d.timestamp !== 'number' ||
    typeof d.testType !== 'string' ||
    typeof d.sessionStartLogged !== 'boolean'
  ) {
    return false
  }

  if (d.notSureCount !== undefined && typeof d.notSureCount !== 'number') {
    return false
  }

  if (d.result !== null && d.result !== undefined && typeof d.result !== 'string') {
    return false
  }

  return true
}

const readRawSession = (sessionId: string): PersistedSessionState | null => {
  try {
    const raw = localStorage.getItem(buildSessionKey(sessionId))
    if (!raw) {
      return null
    }

    const parsed: unknown = JSON.parse(raw)
    return isValidPersistedState(parsed) ? parsed : null
  } catch {
    return null
  }
}

/** Called on app boot — removes expired sessions and orphaned timer keys */
export const purgeExpiredSessions = (): void => {
  if (!tryLocalStorage()) {
    return
  }

  try {
    const now = Date.now()
    const keysToRemove: Array<string> = []
    const activeSessionIds = new Set<string>()

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (!key || !isSessionKey(key)) {
        continue
      }

      try {
        const raw = localStorage.getItem(key)
        if (!raw) {
          keysToRemove.push(key)
          continue
        }

        const parsed: unknown = JSON.parse(raw)
        if (!isValidPersistedState(parsed)) {
          keysToRemove.push(key)
          continue
        }

        if (now - parsed.timestamp > SESSION_TTL_MS) {
          keysToRemove.push(key)
          const sessionId = key.replace(SESSION_KEY_PREFIX, '')
          clearTimerKeysForSession(sessionId)
        } else {
          const sessionId = key.replace(SESSION_KEY_PREFIX, '')
          activeSessionIds.add(sessionId)
        }
      } catch {
        keysToRemove.push(key)
      }
    }

    keysToRemove.forEach((key) => localStorage.removeItem(key))

    purgeOrphanedTimerKeys(activeSessionIds)

    if (keysToRemove.length > 0) {
      console.log(`[sessionPersistence] Purged ${keysToRemove.length} expired session(s)`)
    }
  } catch (error) {
    console.warn('[sessionPersistence] Failed to purge expired sessions:', error)
  }
}

/**
 * Detects whether localStorage survives process kills on this device/WebView.
 * Result is reported to Sentry as storage_status tag for production monitoring.
 */
export const checkStoragePersistence = (): StorageStatus => {
  if (!tryLocalStorage()) {
    return 'disabled'
  }

  try {
    const existing = localStorage.getItem(PERSISTENCE_MARKER_KEY)
    localStorage.setItem(PERSISTENCE_MARKER_KEY, String(Date.now()))

    return existing ? 'available' : 'unverified'
  } catch {
    return 'disabled'
  }
}

const clearTimerKeysForSession = (sessionId: string): void => {
  try {
    const timerSuffix = `_${sessionId}`
    const keysToRemove: Array<string> = []

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && isTimerKey(key) && key.endsWith(timerSuffix)) {
        keysToRemove.push(key)
      }
    }

    keysToRemove.forEach((key) => localStorage.removeItem(key))
  } catch {
    // Non-critical cleanup failure
  }
}

const purgeOrphanedTimerKeys = (activeSessionIds: Set<string>): void => {
  try {
    const keysToRemove: Array<string> = []

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (!key || !isTimerKey(key)) {
        continue
      }

      const parts = key.split('_')
      const timerSessionId = parts[parts.length - 1]

      if (timerSessionId && !activeSessionIds.has(timerSessionId)) {
        keysToRemove.push(key)
      }
    }

    keysToRemove.forEach((key) => localStorage.removeItem(key))
  } catch {
    // Non-critical cleanup failure
  }
}
